import {HttpResponse} from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {TranslocoPipe} from '@jsverse/transloco';
import {firstValueFrom, Observable} from 'rxjs';
import {ButtonModule} from 'primeng/button';
import {
  DeleteProjectReferenceDocumentRequestParams,
  DownloadProjectReferenceDocumentRequestParams,
  ProjectReferenceControllerService,
  ProjectReferenceDocumentRequest,
  ProjectReferenceDocumentResponse,
  ProjectReferenceDocumentSummaryRequest,
  ProjectReferenceDocumentSummaryResponse,
  SummarizeProjectReferenceDocumentRequestParams,
  UpdateProjectReferenceDocumentRequestParams,
  UploadProjectReferenceDocumentRequestParams,
} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';
import {FileDownloadService} from '../../../../utils/file-download.service';
import {DocumentUploadCardComponent} from '../../../card/document-upload-card/document-upload-card.component';
import {
  DocumentUploadCardEdit,
  DocumentUploadCardItem,
  DocumentUploadCardUpload,
} from '../../../card/document-upload-card/document-upload-card.types';
import {DocumentPreviewDialogService} from '../../../dialog/document-preview-dialog/document-preview-dialog.service';
import {LanguageSelectDialogComponent} from '../../../dialog/language-select-dialog/language-select-dialog.component';
import {ModelFieldTextareaComponent} from '../../../inputs/model-field/model-field-textarea/model-field-textarea.component';
import {CARD_VISIBILITY, REFERENCE_DOCUMENT_VISIBILITY} from './project-reference-document.types';

@Component({
  selector: 'app-project-reference-document',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonModule,
    DocumentUploadCardComponent,
    LanguageSelectDialogComponent,
    ModelFieldTextareaComponent,
    TranslocoPipe,
  ],
  templateUrl: './project-reference-document.component.html',
})
export class ProjectReferenceDocumentComponent {
  private readonly projectReferenceController = inject(ProjectReferenceControllerService);
  private readonly documentPreviewDialogService = inject(DocumentPreviewDialogService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly toastService = inject(ToastService);

  readonly referenceId = input.required<number>();
  readonly position = input.required<number>();
  readonly readOnly = input<boolean>(false);

  readonly document = model<ProjectReferenceDocumentResponse | null>(null);
  readonly documentSummary = model<string | null | undefined>();

  readonly documentRemoved = output<void>();

  protected readonly isLanguageDialogVisible = signal<boolean>(false);
  protected readonly isSummarising = signal<boolean>(false);

  protected readonly documentItems = computed<DocumentUploadCardItem[]>(() => {
    const current: ProjectReferenceDocumentResponse | null = this.document();

    if (current === null) {
      return [];
    }

    const item: DocumentUploadCardItem = {
      id: this.referenceId(),
      fileName: current.fileName,
      originalFileName: current.originalFileName,
      fileSizeKb: current.fileSize / 1024,
      createdAt: current.uploadedAt,
      version: current.version,
      statusUuid: current.statusDirectusUuid,
      visibility: CARD_VISIBILITY[current.visibility],
    };

    return [item];
  });

  private readonly summaryText = computed<string>(() => {
    const summary: string = this.documentSummary() ?? '';

    return summary.trim();
  });

  protected readonly isEditable = computed<boolean>(() => !this.readOnly());

  protected readonly isSummaryRequired = computed<boolean>(
    () => this.isEditable() && this.document() !== null,
  );

  protected readonly canSummarise = computed<boolean>(
    () => this.isEditable() && this.document() !== null && this.summaryText() === '',
  );

  protected async onUpload(upload: DocumentUploadCardUpload): Promise<void> {
    const request: UploadProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
      file: upload.file,
      originalFileName: upload.file.name,
      fileName: upload.fileName,
      version: upload.version,
      statusDirectusUuid: upload.statusUuid,
      visibility: REFERENCE_DOCUMENT_VISIBILITY[upload.visibility],
    };
    const upload$: Observable<ProjectReferenceDocumentResponse> =
      this.projectReferenceController.uploadProjectReferenceDocument(request);
    const uploaded: ProjectReferenceDocumentResponse = await firstValueFrom(upload$);

    this.document.set(uploaded);
    void this.toastService.showSuccessMessage();
  }

  protected async onPreview(): Promise<void> {
    const request: DownloadProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
    };
    const download$: Observable<Blob> =
      this.projectReferenceController.downloadProjectReferenceDocument(request);
    const blob: Blob = await firstValueFrom(download$);

    this.documentPreviewDialogService.open(blob);
  }

  protected async onDownload(): Promise<void> {
    const request: DownloadProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
    };
    const download$: Observable<HttpResponse<Blob>> =
      this.projectReferenceController.downloadProjectReferenceDocument(request, 'response');
    const response: HttpResponse<Blob> = await firstValueFrom(download$);

    this.fileDownloadService.downloadFromResponse(response);
  }

  protected async onEdit(edit: DocumentUploadCardEdit): Promise<void> {
    const documentRequest: ProjectReferenceDocumentRequest = {
      fileName: edit.fileName,
      version: edit.version,
      statusDirectusUuid: edit.statusUuid,
      visibility: REFERENCE_DOCUMENT_VISIBILITY[edit.visibility],
    };
    const request: UpdateProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
      projectReferenceDocumentRequest: documentRequest,
    };
    const update$: Observable<ProjectReferenceDocumentResponse> =
      this.projectReferenceController.updateProjectReferenceDocument(request);
    const updated: ProjectReferenceDocumentResponse = await firstValueFrom(update$);

    this.document.set(updated);
    void this.toastService.showSuccessMessage();
  }

  protected async onRemove(): Promise<void> {
    const request: DeleteProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
    };
    const delete$: Observable<unknown> =
      this.projectReferenceController.deleteProjectReferenceDocument(request);

    await firstValueFrom(delete$);
    this.document.set(null);
    this.documentSummary.set('');
    this.documentRemoved.emit();
  }

  protected onSummarise(): void {
    this.isLanguageDialogVisible.set(true);
  }

  protected async onLanguageSelected(language: string): Promise<void> {
    const summaryRequest: ProjectReferenceDocumentSummaryRequest = {
      language: language,
    };
    const request: SummarizeProjectReferenceDocumentRequestParams = {
      referenceId: this.referenceId(),
      projectReferenceDocumentSummaryRequest: summaryRequest,
    };
    const summary$: Observable<ProjectReferenceDocumentSummaryResponse> =
      this.projectReferenceController.summarizeProjectReferenceDocument(request);

    this.isSummarising.set(true);

    try {
      const response: ProjectReferenceDocumentSummaryResponse = await firstValueFrom(summary$);

      this.documentSummary.set(response.summary);
    } finally {
      this.isSummarising.set(false);
    }
  }
}
